import { useEffect } from 'react';
import { useLocation } from 'react-router';

import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';

/**
 * Hides the host's native webview header for good, as soon as the miniapp SDK
 * client is available (SMB-893).
 *
 * On mobile the tenant app renders a native header (title + back button) above
 * the iframe. The miniapp draws its own headers and close affordances, so that
 * native bar is a duplicate. We only ever call `hideHeader()` — never
 * `showHeader()` — so there is no native chrome at all once we own it.
 *
 * Hiding once isn't enough: the native shell re-shows the bar on its own after
 * certain round-trips — closing the booking-success/result modal, the
 * add-to-calendar hand-off, the 3DS/Spreedly payment challenge, and
 * background→foreground. So we re-assert the hidden state on every route change
 * and on `pageshow` / `visibilitychange`. (Mirrors the legacy service-booking
 * fix, HqOapp/service-booking#755.)
 *
 * Escape hatch: with the native header gone the only way out is the SDK close
 * button surfaced on the in-app error screen (`/error`) and success modals.
 * Failed/timed-out requests surface as API errors that route to `/error`.
 */
export const useHideNativeHeader = (): void => {
  const client = useMiniappSdk();
  const { pathname } = useLocation();

  // Hide on first client-resolve and re-assert on every route change.
  useEffect(() => {
    client?.header?.hideHeader();
  }, [client, pathname]);

  // Re-assert when the webview is restored from the page cache (`pageshow`) or
  // returns to the foreground (`visibilitychange`), since the shell re-shows
  // the bar across those transitions.
  useEffect(() => {
    if (!client) return undefined;

    const hide = (): void => client.header?.hideHeader();
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') hide();
    };

    window.addEventListener('pageshow', hide);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', hide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [client]);
};
